import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { DescriptorsComponent } from '../../../../components/descriptor/descriptor.component';
import { LoadingComponent } from '../../../../components/loading/loading.component';
import { REPORT_NAMES } from '../../../../components/reports/constants/report-names.constants';
import { useDataProvider } from '../../../../hooks/data-provider/data-provider.hook';
import { FilterSelectedValue } from '../../../../hooks/filters/interfaces/filter-selected-value.interface';
import { MassEditBlock } from '../mass-edit-sidebar/mass-edit-sidebar.component';
import ScenarioPlannerEditInputFieldComponent from './scenario-planner-edit-input-field.component';
interface MainElastRow {
  id: string;
  name?: string;
  category_name?: string;
  [key: string]: any;
}
interface ScenarioPlannerEditMainElastComponentProps {
  //show: boolean;
  onClose: (show: boolean) => void;
  scenarioPlannerId: string;
  onComplete: (
    inputFields: MassEditBlock[],
    invalidateQuery: () => Promise<void>,
  ) => void;
  disabled?: boolean;
  resetButton: ReactNode;
  applyToFilterSelection: FilterSelectedValue[];
}
export const ScenarioPlannerEditMainElastComponent = ({
  //show,
  onClose,
  scenarioPlannerId,
  onComplete,
  disabled,
  resetButton,
  applyToFilterSelection,
}: ScenarioPlannerEditMainElastComponentProps) => {
  // Dynamic form state based on API response measure columns. This is updated by the user inputs.
  const [mainElast, setMainElast] = useState<{
    [key: string]: string;
  }>();
  // This stores initial main elasticities and once it is loaded doesn't change.
  const [initMainElast, setInitMainElast] = useState<{
    [key: string]: string;
  }>();

  const sortBy = useMemo(() => [{ id: 'name', desc: false }], []);

  const { data, isFetching, invalidateQuery } = useDataProvider<MainElastRow>(
    REPORT_NAMES.SCENARIO_PLANNER_MAIN_ELASTICITY,
    applyToFilterSelection,
    sortBy,
    !disabled,
    true,
    scenarioPlannerId || '',
  );
  const columns = useMemo(() => {
    return data?.columns ?? [];
  }, [data]);

  const measureColumns = useMemo(() => {
    return data?.measureColumns ?? [];
  }, [data]);
  // We expect only one row since a product has one or none data.
  const currentRowData = useMemo(() => {
    return data?.rows[0];
  }, [data?.rows]);

  // Initialize starting metrics form data when API data loads
  useEffect(() => {
    if (isFetching) {
      // Clear values to be prepared for upcoming result.
      setMainElast(undefined);
      setInitMainElast(undefined);
      return;
    }
    const measureColumns = data?.measureColumns;
    if (!measureColumns || measureColumns.length === 0 || !currentRowData) {
      return;
    }
    const values: Record<string, string> = {};
    measureColumns.forEach((i) => {
      const isOverwritten: boolean =
        currentRowData[`${i.id}IsOverwritten`] ?? false;
      if (isOverwritten) {
        values[i.id] = currentRowData[i.id];
      }
    });
    // Values to be modified by the user.
    setMainElast(values);
    // Initial values to display saved one.
    setInitMainElast(values);
  }, [isFetching, currentRowData, data?.measureColumns]);

  const handleChange = useCallback((field: string, value: any) => {
    setMainElast((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleNext = useCallback(() => {
    if (!currentRowData || !mainElast) return;
    const inputFields: MassEditBlock[] = [];
    // Dynamically create input fields based on elasticity values
    Object.entries(mainElast).forEach(([fieldId, value]) => {
      const isOverwritten: boolean =
        currentRowData[`${fieldId}IsOverwritten`] ?? false;
      if (value && value !== '') {
        // New values
        inputFields.push({
          fieldId: `main_${fieldId}`, // Prefix with 'main_' for starting metrics
          inputType: 'abs',
          value: parseFloat(value),
        });
      }
      if (isOverwritten && value === '') {
        // Erased values
        inputFields.push({
          fieldId: `main_${fieldId}`, // Prefix with 'main_' for starting metrics
          inputType: 'abs',
          value: undefined,
        });
      }
    });
    onComplete(inputFields, invalidateQuery);
  }, [currentRowData, mainElast, onComplete, invalidateQuery]);

  const internalDisabled = useMemo(() => {
    return isFetching || disabled;
  }, [isFetching, disabled]);
  return (
    <div className="p-4">
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-12">
            <LoadingComponent
              isLoading={isFetching}
              message="Loading elasticity data..."
            >
              <div className="table-container">
                <table className="table table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.id}
                          className={`${col.descriptors ? 'descriptor' : ''}`}
                        >
                          {col.name}
                        </th>
                      ))}
                      {measureColumns.map((col) => (
                        <th key={col.id}>{col.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {columns.map((col) => (
                        <td
                          key={col.id}
                          className={`${col.descriptors ? 'descriptor' : ''}`}
                        >
                          {col.descriptors && (
                            <div className="prod-cell-text">
                              <div>
                                <div>
                                  {currentRowData?.metadata
                                    ? currentRowData?.metadata.description
                                    : ''}
                                </div>
                                <DescriptorsComponent
                                  metadata={currentRowData?.metadata}
                                />
                              </div>
                            </div>
                          )}
                          {col.descriptors === false &&
                            currentRowData &&
                            currentRowData[col.id]}
                        </td>
                      ))}
                      {measureColumns.map((col) => {
                        return (
                          <td key={col.id}>
                            {initMainElast && (
                              <ScenarioPlannerEditInputFieldComponent
                                value={initMainElast[col.id]}
                                isOverwritten={
                                  currentRowData &&
                                  currentRowData[`${col.id}IsOverwritten`]
                                }
                                placeholder={
                                  currentRowData
                                    ? currentRowData[`${col.id}Original`]
                                    : ''
                                }
                                onChange={handleChange}
                                fieldId={col.id}
                                disabled={internalDisabled}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </LoadingComponent>
          </div>
        </div>
        <div className="row">
          <div className="col-12 d-flex justify-content-end gap-3">
            {resetButton}
            <Button
              variant="outline-danger"
              onClick={() => (onClose ? onClose(false) : null)}
              disabled={internalDisabled}
            >
              Discard Changes
            </Button>
            <Button variant="primary" onClick={handleNext}>
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
